import { Box, VStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { MarkdownPreview } from '../../../../markdown-preview';
import { isMarkdown } from '../content-detection';

type JSONRendererProps = {
  data: unknown;
  maxHeight?: string;
  showLabel?: boolean;
};

/**
 * Check if the JSON object is "simple" (single field or simple key-value pairs)
 * and extract the main text content if so
 */
function extractSimpleContent(data: unknown): string | null {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return null;
  }

  const obj = data as Record<string, unknown>;
  const keys = Object.keys(obj);

  // Single field object - extract its value
  if (keys.length === 1) {
    const value = obj[keys[0]];

    if (typeof value === 'string' && value.length > 20) {
      return value;
    }
  }

  // Check for common "content" fields in objects with 2-3 fields
  if (keys.length <= 3) {
    const contentFields = [
      'query',
      'content',
      'message',
      'text',
      'description',
    ];

    for (const field of contentFields) {
      if (keys.includes(field)) {
        const value = obj[field];

        if (typeof value === 'string' && value.length > 20) {
          return value;
        }
      }
    }
  }

  return null;
}

export function JSONRenderer({
  data,
  maxHeight,
  showLabel = false,
}: JSONRendererProps) {
  // Try to parse if it's a JSON string
  let parsedData = data;

  if (typeof data === 'string') {
    try {
      parsedData = JSON.parse(data);
    } catch {
      // If parsing fails, keep as string
      parsedData = data;
    }
  }

  const jsonString =
    typeof parsedData === 'string'
      ? parsedData
      : JSON.stringify(parsedData, null, 2);

  // Check for empty object
  const isEmptyObject =
    typeof parsedData === 'object' &&
    parsedData !== null &&
    !Array.isArray(parsedData) &&
    Object.keys(parsedData).length === 0;

  const simpleContent = extractSimpleContent(parsedData);
  const hasSimpleContent = simpleContent !== null;
  const contentIsMarkdown = simpleContent ? isMarkdown(simpleContent) : false;

  const renderContent = () => {
    if (hasSimpleContent && simpleContent) {
      // Show extracted content as markdown or plain text
      if (contentIsMarkdown) {
        return <MarkdownPreview source={simpleContent} fontSize={12} compact />;
      } else {
        return (
          <Text
            fontSize="xs"
            color="fg.default"
            whiteSpace="pre-wrap"
            lineHeight="1.6"
          >
            {simpleContent}
          </Text>
        );
      }
    }

    // Default: prettified JSON
    return (
      <MarkdownPreview
        source={`\`\`\`json\n${jsonString}\n\`\`\``}
        fontSize={12}
        compact
      />
    );
  };

  // Handle empty object case
  if (isEmptyObject) {
    return (
      <Box width="100%">
        <Text fontSize="xs" color="fg.muted" fontStyle="italic">
          {'Input: empty'}
        </Text>
      </Box>
    );
  }

  return (
    <VStack alignItems="start" gap={2} width="100%">
      {showLabel && (
        <Text fontSize="xs" fontWeight="medium" color="fg.default">
          {hasSimpleContent ? 'Text' : 'JSON'}
        </Text>
      )}
      <Box
        bg="bg.canvas"
        p={2}
        borderRadius="sm"
        borderWidth="1px"
        borderColor="border.subtle"
        width="100%"
        maxHeight={maxHeight}
        overflow={maxHeight ? 'auto' : undefined}
        position="relative"
      >
        {renderContent()}
      </Box>
    </VStack>
  );
}
